import {
  Directive,
  ElementRef, EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit, Output,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { fromEvent, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appResize]',
})
export class ResizeDirective implements OnInit, OnDestroy {
  @Input('parent') parent: any;

  @Input('maxSizePercentage') maxSize = 30;
  @Input('mixSize') mixSize = 180;

  @Input('right-to-left') rtl: boolean;
  @Input('left-to-right') ltr: boolean;

  @Output() sizeChange:EventEmitter<string> = new EventEmitter();

  private element: HTMLElement;

  private subscriptions: Subscription[] = [];

  constructor(private elementRef: ElementRef, @Inject(DOCUMENT) private document: any) {}

  @HostListener('mouseenter') onMouseEnter() {
    this.highlight('var(--text-light-grey)');
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.highlight(null);
  }

  ngOnInit(): void {
    this.element = this.elementRef.nativeElement as HTMLElement;
    this.ltr = this.ltr !== undefined;
    this.rtl = this.rtl !== undefined;
    if (this.rtl || this.ltr) {
      this.initDrag();
    } else {
      console.error('Resize Direction not defined');
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((s) => {
        if (s) {
          s.unsubscribe();
        }
      });
    }
  }

  private highlight(color: string) {
    this.elementRef.nativeElement.style.backgroundColor = color;
  }

  private initDrag(): void {
    const dragStart$ = fromEvent<MouseEvent>(this.element, 'mousedown');
    const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');
    const drag$ = fromEvent<MouseEvent>(this.document, 'mousemove').pipe(takeUntil(dragEnd$));

    let initialX: number,
      currentX = this.maxSize;

    let dragSub: Subscription;

    const dragStartSub = dragStart$.subscribe((event: MouseEvent) => {
      initialX = event.clientX - currentX;
      this.parent.classList.add('dragging');

      dragSub = drag$.subscribe((event: MouseEvent) => {
        event.preventDefault();

        currentX = this.resizeDirection(event.clientX, initialX);

        const maxWidth = (this.maxSize / 100) * window.innerWidth;
        let newWidth = `${currentX}px`;

        if (currentX > maxWidth) {
          newWidth = `${maxWidth}px`;
        } else if (currentX < this.mixSize) {
          newWidth = `${this.mixSize}px`;
        }

        this.parent.style.width = newWidth;
        this.sizeChange.emit(newWidth)
      });
    });

    const dragEndSub = dragEnd$.subscribe(() => {
      initialX = currentX;
      this.parent.classList.remove('dragging');
      if (dragSub) {
        dragSub.unsubscribe();
      }
    });

    this.subscriptions.push.apply(this.subscriptions, [dragStartSub, dragSub, dragEndSub]);
  }

  private resizeDirection(clientX: number, initialX: number): number {
    return this.ltr ? clientX - initialX : window.innerWidth - clientX;
  }
}
